// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { useMap } from '@/contexts/map-context';
import { Loader } from '@googlemaps/js-api-loader';
import { Box, Paper } from '@mui/material';
import { PropsWithChildren, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

const DEFAULT_SIZE = '1.2rem';

const loader = new Loader({ apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY });

interface Props {
  /**
   * The width and height of the marker.
   *
   * @default '1.2rem'
   */
  size?: string | number;

  /**
   * The background color of the marker.
   */
  color: string;

  /**
   * The latitude of the marker.
   */
  latitude: number;

  /**
   * The longitude of the marker.
   */
  longitude: number;

  /**
   * A callback fired when the marker is clicked.
   */
  onClick?: () => void;
}

/**
 * A map marker that displays a circle on a Google Maps instance.
 */
export function MapMarker(props: PropsWithChildren<Props>) {
  const { map } = useMap();

  const containerRef = useRef(document.createElement('div'));
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(
    null
  );

  // Store the latest `onClick` prop in a ref so that we can always call the
  // freshest callback without having to recreate the Google Maps marker each
  // time the parent component re-renders. This prevents flickering that was
  // occurring because the effect below removed and re-added the marker
  // whenever the `onClick` reference changed.
  const onClickRef = useRef<Props['onClick']>(props.onClick);

  // Update the ref whenever the callback changes. This is lightweight and
  // does **not** require re-creating the actual marker on the map.
  useEffect(() => {
    onClickRef.current = props.onClick;
  }, [props.onClick]);

  useEffect(() => {
    loader.importLibrary('marker').then(({ AdvancedMarkerElement }) => {
      const position: google.maps.LatLngLiteral = {
        lat: props.latitude,
        lng: props.longitude,
      };

      if (markerRef.current === null) {
        const marker = new AdvancedMarkerElement({
          map,
          position,
          content: containerRef.current,
          gmpClickable: true,
        });

        markerRef.current = marker;

        // Attach a single click listener that always delegates to the latest
        // `onClick` callback.
        markerRef.current.addListener('click', () => {
          onClickRef.current?.();
        });
      } else {
        // Marker already exists – just update its mutable properties.
        markerRef.current.map = map;
        markerRef.current.position = position;
      }
    });

    return () => markerRef.current?.remove();
  }, [map, props.latitude, props.longitude]);

  const size = props.size ?? DEFAULT_SIZE;

  return createPortal(
    <Box
      component={Paper}
      width={size}
      height={size}
      bgcolor={props.color}
      display="flex"
      variant="outlined"
      alignItems="center"
      justifyContent="center"
      borderRadius="calc(1px * infinity)"
    >
      {props.children}
    </Box>,
    containerRef.current
  );
}
